import React, {useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import CreateGuestUserModal from './CreateGuestUserModal';
import ClientContactModalProjectsLoader from './ClientContactModalProjectsLoader';
import Util from '../../shared/util/util';
import CreatePersonMutation from '../../../mutations/create_person_mutation.modern';
import CreateProjectPersonClientMutation from '../../../mutations/create_project_person_client_mutation';
import {createToast} from '../../shared/components/toasts/another-toast/toaster';
import {CLIENT_PROFILE_ID} from './constants';
import type {GuestUserFormValues} from './hooks/useGuestUserForm';

const TOAST_DURATION_MS = 5000;

export type CreateGuestUserModalContainerProps = {
	companyId: string;
	clientId: string;
	clientName: string;
	onClose: () => void;
};

type CreatePersonResult = {
	createPerson?: {
		errors?: ReadonlyArray<string> | null;
		person?: {node?: {id?: string | null} | null} | null;
	} | null;
};

/**
 * Submits CreatePersonMutation and resolves with the result so the caller
 * can inspect `result.createPerson.errors` for inline email-duplicate handling.
 * Rejects only on transport failure (Util.CommitMutation's onError).
 */
const commitCreatePerson = (input: object): Promise<CreatePersonResult> =>
	new Promise((resolve, reject) => {
		Util.CommitMutation(
			CreatePersonMutation,
			input,
			(result: CreatePersonResult) => resolve(result),
			true,
			null,
			(err: unknown) => reject(err)
		);
	});

const commitProjectPersons = (personId: string, projectIds: ReadonlyArray<string>): Promise<void> =>
	new Promise((resolve, reject) => {
		Util.CommitMutation(
			CreateProjectPersonClientMutation,
			{personId, projectIds},
			() => resolve(),
			true,
			null,
			(err: unknown) => reject(err)
		);
	});

const CreateGuestUserModalContainer = ({
	companyId,
	clientId,
	clientName,
	onClose,
}: CreateGuestUserModalContainerProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [serverEmailError, setServerEmailError] = useState('');
	const submitLockRef = useRef(false);

	const releaseLock = () => {
		submitLockRef.current = false;
		setIsSubmitting(false);
	};

	const handleSubmit = async (values: GuestUserFormValues) => {
		if (submitLockRef.current) return;
		submitLockRef.current = true;
		setIsSubmitting(true);
		setServerEmailError('');

		const personInput = {
			companyId,
			clientId,
			email: values.email,
			firstName: values.firstName,
			lastName: values.lastName,
			phone: values.phone || null,
			profileIds: [CLIENT_PROFILE_ID],
			invited: true,
		};

		let result: CreatePersonResult;
		try {
			result = await commitCreatePerson(personInput);
		} catch {
			// Transport / non-domain failure. Util.CommitMutation already shows the
			// generic mutation-error modal; we just need to release the submit lock
			// so the user can retry.
			releaseLock();
			return;
		}

		const errors = result.createPerson?.errors ?? [];
		if (errors.length > 0) {
			// Backend surfaced a domain error (almost always duplicate email).
			// Mirror legacy `add_client_user_modal.js:42-47`: inline the email
			// field error, do not toast, keep modal open for retry.
			setServerEmailError(formatMessage({id: 'client_contact.error.duplicate_email'}));
			releaseLock();
			return;
		}

		const personId = result.createPerson?.person?.node?.id;
		if (!personId) {
			releaseLock();
			createToast({
				duration: TOAST_DURATION_MS,
				message: formatMessage({id: 'guest_user.snackbar.create_failed'}),
			});
			return;
		}
		const selectedProjectIds = values.projectIds ?? [];
		if (selectedProjectIds.length === 0) {
			releaseLock();
			createToast({
				duration: TOAST_DURATION_MS,
				message: formatMessage({id: 'guest_user.snackbar.created'}),
			});
			onClose();
			return;
		}

		try {
			await commitProjectPersons(personId, selectedProjectIds);
		} catch {
			// Person was created but at least one project assignment failed. Mirror
			// Edit's behaviour: surface a generic failure toast, leave the modal
			// open so the user can retry / adjust. We deliberately do NOT roll back
			// the created Person — that's the same trade-off Edit makes.
			releaseLock();
			createToast({
				duration: TOAST_DURATION_MS,
				message: formatMessage({id: 'guest_user.snackbar.create_failed'}),
			});
			return;
		}

		releaseLock();
		createToast({
			duration: TOAST_DURATION_MS,
			message: formatMessage({id: 'guest_user.snackbar.created'}),
		});
		onClose();
	};

	return (
		<ClientContactModalProjectsLoader clientId={clientId}>
			{({projectOptions, isProjectsLoading}) => (
				<CreateGuestUserModal
					clientName={clientName}
					projectOptions={projectOptions}
					isProjectsLoading={isProjectsLoading}
					onClose={onClose}
					onSubmit={handleSubmit}
					isSubmitting={isSubmitting}
					serverEmailError={serverEmailError}
					onClearServerEmailError={() => setServerEmailError('')}
				/>
			)}
		</ClientContactModalProjectsLoader>
	);
};

export default CreateGuestUserModalContainer;
